import { useTranslation } from "react-i18next";
import { AmwayFilledLogoIcon } from "../../../../common/icons/AmwayFilledLogoIcon/AmwayFilledLogoIcon";
import LeafTag from "../../../../common/leaf/LeafTag/LeafTag";
import defaultContent from "../../../../content/downlineProfile";
import { useState } from "react";
import { IconChevronDown } from "../../../../common/icons/IconChevronDown/IconChevronDown";
import { Address, PartyInfo } from "../../../../services/Profile/profileAPI.types";
import ContactRow from "../../shared/contact/ContactRow";
import { RootState, useAppSelector } from "../../../../store";
import { resolveApplicantDisplayName } from "../../../../common/util/displayName";
import { storedLocale } from "../../../Authentication/util";
import { TFunction } from "i18next";
import { trackApplicantViewMore } from "../../PlatinumProfile/downlineProfileTealium";
import { getFormattedAddress } from "../../../Lit/acc-mixins/src/modules/formatters";
import { getCityStateAddress } from "../../../../common/util/address";
interface KeyInformationApplicantRowProps {
  name?: string;
  isPrimary?: boolean;
  address?: Address;
  phone?: string;
  whatsApp?: string;
  email?: string;
  privacyFlag?: boolean;
  imageRefUrl?: string;
  primaryApplicantImage?: string;
  isKoreaMarket?: boolean;
}

const KeyInformationApplicantRow = ({
  name,
  isPrimary,
  address,
  phone,
  whatsApp,
  email,
  privacyFlag = false,
  imageRefUrl = "",
  primaryApplicantImage,
  isKoreaMarket = false,
}: KeyInformationApplicantRowProps) => {
  const { t } = useTranslation(["downlineProfile"]);
  const { showCallIcon, showMessageIcon, showEmailIcon, hideDownlineContactDetails } =
    useAppSelector((state: RootState) => state.boot.configuration.profile) ?? {};
  const hideWhatsApp = useAppSelector((state: RootState) => state.boot.configuration.hideWhatsApp);
  const effectiveHideDownlineContactDetails = hideDownlineContactDetails;
  const { address: addressFormat = "", addressLinesDivider } =
    useAppSelector((state) => state?.boot?.configuration?.profile?.format) ?? {};
  const defaultAddresses = {
    addressLines: [],
    municipality: "",
    region: "",
    postalCode: "",
    country: "",
    type: "",
  };

  const {
    addressLines: address_lines,
    municipality,
    region,
    postalCode: postal_code,
    country,
  } = address || defaultAddresses;

  const preFormattedAddress = {
    address_lines,
    municipality,
    region,
    postal_code,
    country,
  };

  const addressFormatted = addressFormat
    .split(" ")
    .map((format: string) => format.replace(/[A-Z]/g, (address) => `_${address.toLocaleLowerCase()}`))
    .join(" ");

  const formattedAddress = getFormattedAddress(preFormattedAddress, {
    string: addressFormatted,
    divider: addressLinesDivider,
  });

  const showValidAddress = formattedAddress && formattedAddress !== "--" && privacyFlag !== true;
  const displayAddress = showValidAddress ? formattedAddress.replace(/ \| /g, ", ") : "";
  const effectiveAddress =
    isKoreaMarket && effectiveHideDownlineContactDetails ? getCityStateAddress(address) : displayAddress;
  return (
    <>
      <div className="applicant-row">
        <div className="applicant-row--content">
          <div>
            <div className="profile-icon">
              {imageRefUrl !== "" || primaryApplicantImage ? (
                <img src={primaryApplicantImage && !imageRefUrl ? primaryApplicantImage : imageRefUrl} alt="" />
              ) : (
                <AmwayFilledLogoIcon />
              )}
            </div>
            <div className="applicant-row--content--info">
              <div>
                <div>
                  <p>{name}</p>
                  <div>
                    <LeafTag variant="medOrange">
                      <span>
                        {isPrimary
                          ? `${t("keyInformation.applicantActions.primary", defaultContent.keyInformation.applicantActions.primary)}`
                          : `${t("keyInformation.applicantActions.coApplicant", defaultContent.keyInformation.applicantActions.coApplicant)}`}
                      </span>
                    </LeafTag>
                  </div>
                </div>
              </div>
              {privacyFlag !== true && effectiveAddress && <p>{effectiveAddress}</p>}
            </div>
          </div>
          <ContactRow
            phone={phone}
            email={email}
            isKoreaMarket={isKoreaMarket}
            iconFlags={{
              showCallIcon,
              showMessageIcon,
              showEmailIcon,
              hideWhatsApp,
              hideDownlineContactDetails: effectiveHideDownlineContactDetails,
            }}
            whatsApp={whatsApp}
            tealium={{ type: "applicant", isPrimary }}
          />
        </div>
      </div>
    </>
  );
};

export const getApplicantAboName = (
  props: PartyInfo,
  privacyFlag: boolean,
  nameFormat: string,
  portalLocale: string,
  t: TFunction<readonly ["downlineProfile", "common"], undefined>,
): string => {
  const { displayName, localeDisplayName, localeName, latinName, preferredLanguageCode } = props;
  const aboName = resolveApplicantDisplayName(
    displayName,
    localeDisplayName,
    localeName,
    latinName,
    nameFormat,
    privacyFlag,
    preferredLanguageCode,
    portalLocale,
    t,
  );
  return aboName;
};
interface KeyInformationApplicantWrapperProps {
  partyInfo?: PartyInfo[];
  privacyFlag?: boolean;
  partyImages?: { aff: string; abo: string; partyId: string; profileImage: string }[];
  primaryApplicantImage?: string;
  isKoreaMarket?: boolean;
}
const KeyInformationApplicantWrapper = ({
  partyInfo,
  privacyFlag = false,
  partyImages,
  primaryApplicantImage,
  isKoreaMarket = false,
}: KeyInformationApplicantWrapperProps) => {
  const { t } = useTranslation(["downlineProfile", "common"]);
  const [applicantViewMore, setApplicantViewMore] = useState(false);
  const { aboName: nameFormat = "" } = useAppSelector((state) => state?.boot?.configuration?.profile?.format) ?? {};
  const portalLocale = storedLocale().split("-")[0];
  return (
    <>
      {partyInfo?.slice(0, 2).map((v, i) => {
        const addr = v.addresses?.[0];
        const aboName = getApplicantAboName(v, privacyFlag, nameFormat, portalLocale, t);
        return (
          <KeyInformationApplicantRow
            key={i}
            name={aboName}
            isPrimary={v.isPrimary}
            address={addr}
            phone={v.phoneNumbers[0]?.displayNumber}
            whatsApp={v.whatsApp[0]?.ecommAddr}
            email={v.emailAddresses[0]?.email}
            privacyFlag={privacyFlag}
            isKoreaMarket={isKoreaMarket}
            imageRefUrl={partyImages?.find((e) => String(e.partyId) === String(v.partyId))?.profileImage ?? ""}
            primaryApplicantImage={v.isPrimary && primaryApplicantImage ? primaryApplicantImage : undefined}
          />
        );
      })}
      <div
        className={`applicant-view-more-content${applicantViewMore ? " applicant-view-more-content--expanded" : ""}`}
      >
        <div>
          {partyInfo?.slice(2).map((v, i) => {
            const addr = v.addresses[0];
            const aboName = getApplicantAboName(v, privacyFlag, nameFormat, portalLocale, t);
            return (
              <KeyInformationApplicantRow
                key={i}
                name={aboName}
                isPrimary={v.isPrimary}
                address={addr}
                phone={v.phoneNumbers[0]?.displayNumber}
                whatsApp={v.whatsApp[0]?.ecommAddr}
                email={v.emailAddresses[0]?.email}
                privacyFlag={privacyFlag}
                isKoreaMarket={isKoreaMarket}
                imageRefUrl={partyImages?.find((e) => String(e.partyId) === String(v.partyId))?.profileImage ?? ""}
                primaryApplicantImage={v.isPrimary && primaryApplicantImage ? primaryApplicantImage : undefined}
              />
            );
          })}
        </div>
      </div>
      <div className={`applicant-view-more ${(partyInfo ?? [])?.length > 2 ? "applicant-view-more-show" : ""}`}>
        <button
          onClick={() => {
            setApplicantViewMore((prev) => !prev);
            trackApplicantViewMore();
          }}
        >
          <p>
            {applicantViewMore
              ? `${t("keyInformation.applicantActions.showLess", defaultContent.keyInformation.applicantActions.showLess)}`
              : `${t("keyInformation.applicantActions.showMore", defaultContent.keyInformation.applicantActions.showMore)}`}
          </p>
          <div className={applicantViewMore ? "applicant-view-more__flipped" : ""}>
            <IconChevronDown />
          </div>
        </button>
      </div>
    </>
  );
};

export default KeyInformationApplicantWrapper;
